<template>
  <v-form v-model="valid" ref="form">
    <v-container fluid class="px-6 mt-4">
      <v-row dense>
        <v-col cols="12" sm="6" md="4">
          <v-text-field label="Name" v-model="product.name" :rules="[fieldRules.required,fieldRules.maxLength(50)]" :counter="50" outlined dense/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field label="SKU" v-model="product.sku" :rules="[fieldRules.maxLength(20)]" :counter="20" outlined dense/>
        </v-col>
        <v-col cols="2">
          <v-checkbox label="Active" v-model="product.active" dense class="mt-1"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" sm="6" md="4">
          <inventory-categories-select v-model="product.categoryId" label="Category" type="Product" allow-add hint/>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <picture-picker v-model="product.pictureURL" :size="200" @change="product.pictureUpload=product.pictureURL"/>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-select :items="[{text:'Spirit',value:'Spirit'},{text:'Wine',value:'WineHigh'},{text:'Non-excise',value:'NonExcise'}]" v-model="product.alcoholType"
                    outlined dense label="Contents" :disabled="product.existingTransactions" @change="product.alcoholId=''"/>
        </v-col>
        <v-col offset-md="1" sm="4" md="3" v-if="product.alcoholType!=='NonExcise'">
          <v-checkbox label="Allow variable strength" v-model="product.allowVariableStrength" dense class="mt-1">
            <template v-slot:append>
              <v-tooltip bottom max-width="300" color="primary">
                <template v-slot:activator="{on}">
                  <v-icon v-on="on" class="ml-4 mt-n1">mdi-help-circle</v-icon>
                </template>
                <v-icon color="white">mdi-new-box</v-icon>
                If checked, then you can bottle this product at different strengths,
                which will be associated with the product lot code.
              </v-tooltip>
            </template>
          </v-checkbox>
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" sm="6" md="4">
          <v-autocomplete v-if="product.alcoholType === 'Spirit'"
                          v-model="product.alcoholId" outlined dense label="Spirit" :disabled="product.existingTransactions"
                          :items="spirits" item-text="name" item-value="id" :rules="[spirit]"
                          no-data-text="No spirits available">
            <template v-slot:prepend-item>
              <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="spiritCreate=true">
                <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new spirit
              </div>
            </template>
          </v-autocomplete>
          <v-autocomplete v-if="product.alcoholType === 'WineHigh'"
                          v-model="product.alcoholId" outlined dense label="Wine" :disabled="product.existingTransactions"
                          :items="wines" item-text="name" item-value="id" :rules="[spirit]"
                          no-data-text="No wine available">
            <template v-slot:prepend-item>
              <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="wineCreate=true">
                <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new wine
              </div>
            </template>
          </v-autocomplete>
          <v-autocomplete v-else-if="product.alcoholType === 'NonExcise'"
                          v-model="product.alcoholId" outlined dense label="Non-excise" :disabled="product.existingTransactions"
                          :items="nonExcises" item-text="name" item-value="id" :rules="[spirit]"
                          no-data-text="No non-excise types available">
            <template v-slot:prepend-item>
              <div class="pa-2 grey lighten-3 text-body-2 font-weight-medium cursor-pointer" @click="nonExciseCreate=true">
                <v-icon color="primary" class="mr-2">mdi-plus-thick</v-icon>Add new non-excise
              </div>
            </template>
          </v-autocomplete>
        </v-col>
        <v-col offset-md="1" cols="6" sm="3" md="2">
          <decimal-number-input label="Bottle size" v-model="product.bottleSize" integer :min="1"
                                prefix="" suffix="ml" :precision="0" :disabled="product.existingTransactions"/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <decimal-number-input label="Strength" v-model="product.strength" :min="0.1" :max="99.9" v-if="product.alcoholType!=='NonExcise'"
                                prefix="" suffix="% alc/vol." :precision="1" :disabled="product.existingTransactions"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6" sm="3" md="2">
          <decimal-number-input label="Case size" v-model="product.caseSize" integer :min="1"
                                prefix="" suffix="bottles" :precision="0" :disabled="product.existingTransactions"/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <decimal-number-input label="Case weight" v-model="product.caseWeight"
                                prefix="" suffix="kg" :precision="3"/>
        </v-col>
        <v-col offset-md="1" cols="6" sm="3" md="2">
          <v-autocomplete dense outlined label="Container type" v-model="product.containerType"
                  :items="[{text:'Glass',value:'Glass'},{text:'Plastic',value:'Plastic'},{text:'Can',value:'Can'},{text:'Other',value:'Other'}]"/>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6" sm="3" md="2">
          <v-text-field label="UPC" v-model="product.upc" :counter="12" :rules="[upc]" outlined dense/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field label="SCC" v-model="product.scc" :counter="14" :rules="[scc]" outlined dense/>
        </v-col>
        <v-col offset-md="1" cols="6" sm="3" md="2">
          <decimal-number-input label="Pallet TI" v-model="product.palletTI" integer :min="1"
                                prefix="" suffix="cases" :precision="0"/>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <decimal-number-input label="Pallet HI" v-model="product.palletHI" integer :min="1"
                                prefix="" suffix="cases" :precision="0"/>
        </v-col>
      </v-row>

      <component :is="province" :product="product" v-if="product.alcoholType==='Spirit'"/>

      <v-row v-if="values && connectedToAccounting">
        <v-col cols="12" sm="6" md="4" if="product.alcoholType === 'Spirit'">
          <account-picker v-model="product.bondedInventoryAccountRef" label="In-bond inventory asset account"
                          :accounts="accounts" :addTypes="['OtherCurrentAsset']"
                          hint="The inventory account used to track the value of product in-bond"/>
        </v-col>
        <v-col cols="12" sm="6" md="4">
          <account-picker v-model="product.dutyPaidInventoryAccountRef" label="Duty-paid inventory asset account"
                          :accounts="accounts" :addTypes="['OtherCurrentAsset']"
                          hint="The inventory account used to track the value of duty-paid product"/>
        </v-col>
      </v-row>

    </v-container>

    <spirit-quick-create v-model="spiritCreate" @create="spiritCreated"/>
    <wine-quick-create v-model="wineCreate" @create="wineCreated"/>
    <alcohol-non-excise-quick-create v-model="nonExciseCreate" @create="nonExciseCreated"/>

    </v-form>
</template>

<script>
import SpiritQuickCreate from '@/views/alcohol/spirits/SpiritQuickCreate'
import WineQuickCreate from '@/views/alcohol/wine/WineQuickCreate'
import AlcoholNonExciseQuickCreate from '@/views/alcohol/nonExcise/AlcoholNonExciseQuickCreate'
import barcode from '@/mixins/barcode'
import InventoryCategoriesSelect from '@/views/inventory/categories/InventoryCategoriesSelect'

export default {
  name: 'InventoryProductsEditInfo',
  components: {SpiritQuickCreate,WineQuickCreate,AlcoholNonExciseQuickCreate,InventoryCategoriesSelect},
  mixins: [barcode],
  props: {
    product: {type:Object, required:true},
    accounts: {type:Array, required:true},
    spirits: {type:Array, required:true},
    wines: {type:Array, required:true},
    nonExcises: {type:Array, required:true},
    values: {type:Boolean, required:true}
  },
  data() {
    return {
      valid: true,
      province: null,
      spirit: v => v != '' || 'Required',
      spiritCreate:false,
      wineCreate:false,
      nonExciseCreate:false
    }
  },
  computed: {
    connectedToAccounting() {
      return this.$store.getters.company.connectedToAccounting;
    },
  },
  watch: {
    product: {
      handler() {this.$emit('input', true);},
      immediate: true,
      deep: true
    }
  },
  created() {
    this.province = () => ({component:import(`@/views/provinces/${this.$store.getters.company.province}/product/Info`)});
    setTimeout(() => {if (this.$refs.form) this.$refs.form.validate()},4000);
  },
  methods: {
    spiritCreated(spirit) {
      this.spirits.push(spirit);
      this.product.alcoholId = spirit.id;
    },
    wineCreated(wine) {
      this.wines.push(wine);
      this.product.alcoholId = wine.id;
    },
    nonExciseCreated(nonExcise) {
      this.nonExcises.push(nonExcise);
      this.product.alcoholId = nonExcise.id;
    }
  }
}
</script>